import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { IPrinterConfig } from '../../models';
import { IPrinterConnectionStrategy } from './printer-connection-strategy.interface';

// WebUSB types
interface USBPrinterDevice {
  device: USBDevice;
  interfaceNumber: number;
  endpointOut: number;
  endpointIn?: number;
}

@Injectable({
  providedIn: 'root',
})
export class WebUSBConnectionStrategy implements IPrinterConnectionStrategy {
  private readonly translateService = inject(TranslateService);
  private readonly usbDevices = new Map<string, USBPrinterDevice>();

  readonly onDisconnect = new Subject<string>(); // Emits printerId

  constructor() {
    if (this.isSupported()) {
      navigator.usb.addEventListener('disconnect', (event) => {
        this.handleUSBDisconnect(event.device);
      });
    }
  }

  isSupported(): boolean {
    return 'usb' in navigator;
  }

  async connect(printer: IPrinterConfig): Promise<boolean> {
    if (!this.isSupported()) {
      throw new Error(this.translateService.instant('printers.errors.webusb_not_supported'));
    }

    if (!printer.usbVendorId || !printer.usbProductId) {
      throw new Error(this.translateService.instant('printers.errors.usb_id_missing'));
    }

    try {
      // Find the device from paired devices
      const devices = await navigator.usb.getDevices();
      let device = devices.find((d) => d.vendorId === printer.usbVendorId && d.productId === printer.usbProductId);

      if (!device) {
        throw new Error(this.translateService.instant('printers.errors.usb_device_not_found'));
      }

      // Try to open the device - if access is denied, we need to re-request permission
      try {
        await device.open();
      } catch (openError) {
        // Check if this is an access denied error (SecurityError)
        if (openError instanceof DOMException && openError.name === 'SecurityError') {
          console.log('WebUSB access denied, requesting permission again...');

          // Request the device again to get fresh permission
          // Filter by the exact vendor/product ID we need
          const reauthorizedDevice = await navigator.usb.requestDevice({
            filters: [{ vendorId: printer.usbVendorId, productId: printer.usbProductId }],
          });

          if (!reauthorizedDevice) {
            throw new Error(this.translateService.instant('printers.errors.usb_auth_cancelled'));
          }

          device = reauthorizedDevice;
          try {
            await device.open();
          } catch (reOpenError) {
            if (reOpenError instanceof DOMException && reOpenError.name === 'SecurityError') {
              throw new Error(this.translateService.instant('printers.errors.usb_access_denied'));
            }
            throw reOpenError;
          }
        } else {
          throw openError;
        }
      }

      // Select configuration (usually 1)
      if (device.configuration === null) {
        await device.selectConfiguration(1);
      }

      // Find printer interface and endpoints
      const printerInterface = this.findPrinterInterface(device);
      if (!printerInterface) {
        await device.close();
        throw new Error(this.translateService.instant('printers.errors.no_printer_interface'));
      }

      try {
        await device.claimInterface(printerInterface.interfaceNumber);
      } catch (claimError) {
        await device.close();
        if (claimError instanceof DOMException && claimError.name === 'SecurityError') {
          throw new Error(this.translateService.instant('printers.errors.usb_claim_failed'));
        }
        throw claimError;
      }

      // Find bulk OUT endpoint for sending data
      const endpointOut = this.findBulkOutEndpoint(printerInterface);
      if (endpointOut === null) {
        await device.close();
        throw new Error(this.translateService.instant('printers.errors.no_output_endpoint'));
      }

      // Store device reference
      const usbPrinter: USBPrinterDevice = {
        device,
        interfaceNumber: printerInterface.interfaceNumber,
        endpointOut,
        endpointIn: this.findBulkInEndpoint(printerInterface) ?? undefined,
      };

      this.usbDevices.set(printer.id, usbPrinter);
      return true;
    } catch (error) {
      console.error('WebUSB connection error:', error);
      throw error;
    }
  }

  async disconnect(printerId: string): Promise<void> {
    const usbDevice = this.usbDevices.get(printerId);
    if (usbDevice) {
      try {
        await usbDevice.device.close();
      } catch (e) {
        console.warn('Error closing USB device:', e);
      }
      this.usbDevices.delete(printerId);
    }
  }

  async sendData(printerId: string, data: Uint8Array): Promise<boolean> {
    const usbPrinter = this.usbDevices.get(printerId);
    if (!usbPrinter) {
      throw new Error(this.translateService.instant('printers.errors.printer_not_connected'));
    }

    try {
      // Create a proper ArrayBuffer from Uint8Array
      const buffer = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
      const result = await usbPrinter.device.transferOut(usbPrinter.endpointOut, buffer);
      return result.status === 'ok';
    } catch (error) {
      console.error('USB transfer error:', error);
      throw error;
    }
  }

  hasConnection(printerId: string): boolean {
    return this.usbDevices.has(printerId);
  }

  /**
   * Request WebUSB device access (requires user gesture)
   * Returns the selected device or null if cancelled
   */
  async requestUSBDevice(): Promise<USBDevice | null> {
    if (!this.isSupported()) {
      throw new Error(this.translateService.instant('printers.errors.webusb_not_supported'));
    }

    try {
      // Common USB vendor IDs for thermal printers
      const filters: USBDeviceFilter[] = [
        { classCode: 7 }, // Printer class
        { vendorId: 0x04b8 }, // Epson
        { vendorId: 0x0519 }, // Star Micronics
        { vendorId: 0x0dd4 }, // Custom Engineering
        { vendorId: 0x0fe6 }, // ICS (Citizen)
        { vendorId: 0x20d1 }, // Sewoo / Bixolon
        { vendorId: 0x0483 }, // STMicroelectronics (generic thermal)
        { vendorId: 0x1fc9 }, // NXP (some thermal printers)
        { vendorId: 0x0456 }, // Analog Devices
        { vendorId: 0x067b }, // Prolific (USB-to-Serial adapters)
        { vendorId: 0x0403 }, // FTDI (USB-to-Serial adapters)
      ];

      const device = await navigator.usb.requestDevice({ filters });
      return device;
    } catch (error) {
      // User cancelled or no device found
      if (error instanceof DOMException && error.name === 'NotFoundError') {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get list of already-paired USB devices
   */
  async getPairedUSBDevices(): Promise<USBDevice[]> {
    if (!this.isSupported()) {
      return [];
    }
    return await navigator.usb.getDevices();
  }

  private findPrinterInterface(device: USBDevice): USBInterface | null {
    if (!device.configuration) return null;

    for (const iface of device.configuration.interfaces) {
      // Check alternates for printer class (7)
      for (const alternate of iface.alternates) {
        if (alternate.interfaceClass === 7) {
          return iface;
        }
      }
    }

    // Fallback: return first interface with bulk endpoints
    for (const iface of device.configuration.interfaces) {
      for (const alternate of iface.alternates) {
        const hasBulkOut = alternate.endpoints.some((ep) => ep.type === 'bulk' && ep.direction === 'out');
        if (hasBulkOut) {
          return iface;
        }
      }
    }

    return null;
  }

  private findBulkOutEndpoint(iface: USBInterface): number | null {
    for (const alternate of iface.alternates) {
      for (const endpoint of alternate.endpoints) {
        if (endpoint.type === 'bulk' && endpoint.direction === 'out') {
          return endpoint.endpointNumber;
        }
      }
    }
    return null;
  }

  private findBulkInEndpoint(iface: USBInterface): number | null {
    for (const alternate of iface.alternates) {
      for (const endpoint of alternate.endpoints) {
        if (endpoint.type === 'bulk' && endpoint.direction === 'in') {
          return endpoint.endpointNumber;
        }
      }
    }
    return null;
  }

  private handleUSBDisconnect(device: USBDevice): void {
    // Find which printer this device belongs to
    for (const [printerId, usbPrinter] of this.usbDevices.entries()) {
      if (usbPrinter.device === device) {
        this.usbDevices.delete(printerId);
        this.onDisconnect.next(printerId);
        break;
      }
    }
  }
}
